/**
 * Arcℏimedian DSaaS — React Query Hooks
 * Typed hooks for data fetching, mutations, and cache invalidation.
 *
 * Install: npm install @tanstack/react-query axios
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  authService, usersService, workspacesService, datasetsService,
  modelsService, experimentsService, pipelinesService, billingService,
  notificationsService, auditService, analysesService,
} from './services';
import { tokenStorage } from './client';
import type * as T from './types';

// ── Query Key Factory ──
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params: Record<string, unknown>) => ['users', 'list', params] as const,
    detail: (id: string) => ['users', id] as const,
    stats: ['users', 'stats'] as const,
  },
  workspaces: {
    all: ['workspaces'] as const,
    list: (params: Record<string, unknown>) => ['workspaces', 'list', params] as const,
    detail: (id: string) => ['workspaces', id] as const,
    members: (id: string) => ['workspaces', id, 'members'] as const,
    invites: (id: string) => ['workspaces', id, 'invites'] as const,
    permissions: (id: string) => ['workspaces', id, 'permissions'] as const,
    stats: ['workspaces', 'stats'] as const,
  },
  datasets: {
    all: ['datasets'] as const,
    list: (params: Record<string, unknown>) => ['datasets', 'list', params] as const,
    detail: (id: string) => ['datasets', id] as const,
    columns: (id: string) => ['datasets', id, 'columns'] as const,
    stats: ['datasets', 'stats'] as const,
  },
  models: {
    all: ['models'] as const,
    list: (params: Record<string, unknown>) => ['models', 'list', params] as const,
    detail: (id: string) => ['models', id] as const,
    versions: (id: string) => ['models', id, 'versions'] as const,
    deployments: (id: string) => ['models', id, 'deployments'] as const,
    stats: ['models', 'stats'] as const,
  },
  experiments: {
    all: ['experiments'] as const,
    list: (params: Record<string, unknown>) => ['experiments', 'list', params] as const,
    detail: (id: string) => ['experiments', id] as const,
    runs: (id: string) => ['experiments', id, 'runs'] as const,
    runDetail: (expId: string, runId: string) => ['experiments', expId, 'runs', runId] as const,
    metricKeys: (id: string) => ['experiments', id, 'metric-keys'] as const,
    leaderboard: (id: string, metric: string) => ['experiments', id, 'leaderboard', metric] as const,
    stats: ['experiments', 'stats'] as const,
  },
  pipelines: {
    all: ['pipelines'] as const,
    list: (params: Record<string, unknown>) => ['pipelines', 'list', params] as const,
    detail: (id: string) => ['pipelines', id] as const,
    steps: (id: string) => ['pipelines', id, 'steps'] as const,
    runs: (id: string) => ['pipelines', id, 'runs'] as const,
    schedule: (id: string) => ['pipelines', id, 'schedule'] as const,
    stats: ['pipelines', 'stats'] as const,
  },
  billing: {
    plans: ['billing', 'plans'] as const,
    subscription: (workspaceId: string) => ['billing', 'subscription', workspaceId] as const,
    usage: (workspaceId: string) => ['billing', 'usage', workspaceId] as const,
    invoices: (workspaceId: string) => ['billing', 'invoices', workspaceId] as const,
    paymentMethods: (workspaceId: string) => ['billing', 'payment-methods', workspaceId] as const,
    stats: ['billing', 'stats'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (params: Record<string, unknown>) => ['notifications', 'list', params] as const,
    counts: ['notifications', 'counts'] as const,
    preferences: ['notifications', 'preferences'] as const,
  },
  audit: {
    list: (params: Record<string, unknown>) => ['audit', 'list', params] as const,
    detail: (id: string) => ['audit', id] as const,
    stats: ['audit', 'stats'] as const,
    feed: (params: Record<string, unknown>) => ['activity', 'feed', params] as const,
  },
  analyses: {
    all: ['analyses'] as const,
    list: (params: Record<string, unknown>) => ['analyses', 'list', params] as const,
    detail: (id: string) => ['analyses', id] as const,
  },
};

// ═══════════════════════════════════════════════
// AUTH HOOKS
// ═══════════════════════════════════════════════

export const useCurrentUser = () =>
  useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => authService.me().then((r) => r.data),
    enabled: !!tokenStorage.getAccess(),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: false,
  });

export const useLogin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: T.LoginRequest) => authService.login(data).then((r) => r.data),
    onSuccess: (tokens) => {
      tokenStorage.set(tokens);
      qc.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
  });
};

export const useRegister = () =>
  useMutation({
    mutationFn: (data: T.RegisterRequest) => authService.register(data).then((r) => r.data),
  });

export const useLogout = () => {
  const qc = useQueryClient();
  return () => {
    tokenStorage.clear();
    qc.clear();
    window.location.href = '/login';
  };
};

export const useChangePassword = () =>
  useMutation({
    mutationFn: (data: T.ChangePasswordRequest) => authService.changePassword(data),
  });

export const usePatchMe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: T.UserUpdate) =>
      authService.updateProfile(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.auth.me }),
  });
};

// ═══════════════════════════════════════════════
// ADMIN HOOKS
// ═══════════════════════════════════════════════

export const useAdminUsers = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => usersService.list(params).then((r) => r.data),
  });

export const useAdminUserStats = () =>
  useQuery({
    queryKey: ['admin', 'users', 'stats'],
    queryFn: () => usersService.stats().then((r) => r.data),
  });

export const useAdminWorkspaceStats = () =>
  useQuery({
    queryKey: ['admin', 'workspaces', 'stats'],
    queryFn: () => workspacesService.stats().then((r) => r.data),
  });

export const useDeactivateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersService.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
};

export const useReactivateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersService.reactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
};

// ═══════════════════════════════════════════════
// WORKSPACE HOOKS
// ═══════════════════════════════════════════════

export const useWorkspaces = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.workspaces.list(params),
    queryFn: () => workspacesService.list(params).then((r) => r.data),
  });

export const useWorkspace = (id: string) =>
  useQuery({
    queryKey: queryKeys.workspaces.detail(id),
    queryFn: () => workspacesService.get(id).then((r) => r.data),
    enabled: !!id,
  });

export const useCreateWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: T.WorkspaceCreate) => workspacesService.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.workspaces.all }),
  });
};

export const useWorkspaceMembers = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.workspaces.members(workspaceId),
    queryFn: () => workspacesService.members.list(workspaceId).then((r) => r.data),
    enabled: !!workspaceId,
  });

export const useWorkspacePermissions = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.workspaces.permissions(workspaceId),
    queryFn: () => workspacesService.permissions.mine(workspaceId).then((r) => r.data),
    enabled: !!workspaceId,
  });

export const useInviteMember = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      workspacesService.invites.create(workspaceId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.invites(workspaceId) });
    },
  });
};

// ═══════════════════════════════════════════════
// DATASET HOOKS
// ═══════════════════════════════════════════════

export const useDatasets = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.datasets.list(params),
    queryFn: () => datasetsService.list(params).then((r) => r.data),
  });

export const useDataset = (id: string) =>
  useQuery({
    queryKey: queryKeys.datasets.detail(id),
    queryFn: () => datasetsService.get(id).then((r) => r.data),
    enabled: !!id,
  });

export const useDatasetColumns = (id: string) =>
  useQuery({
    queryKey: queryKeys.datasets.columns(id),
    queryFn: () => datasetsService.columns(id).then((r) => r.data),
    enabled: !!id,
  });

export const useUploadDataset = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, fields, onProgress }: { file: File; fields: { name: string; workspace_id: string }; onProgress?: (p: number) => void }) =>
      datasetsService.upload(file, fields, onProgress).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.datasets.all }),
  });
};

// ═══════════════════════════════════════════════
// ML MODEL HOOKS
// ═══════════════════════════════════════════════

export const useModels = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.models.list(params),
    queryFn: () => modelsService.list(params).then((r) => r.data),
  });

export const useModel = (id: string) =>
  useQuery({
    queryKey: queryKeys.models.detail(id),
    queryFn: () => modelsService.get(id).then((r) => r.data),
    enabled: !!id,
  });

export const useModelVersions = (modelId: string) =>
  useQuery({
    queryKey: queryKeys.models.versions(modelId),
    queryFn: () => modelsService.versions.list(modelId).then((r) => r.data),
    enabled: !!modelId,
  });

export const useDeployModel = (modelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { version_id: string; environment: string }) =>
      modelsService.deploy(modelId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.models.deployments(modelId) });
      qc.invalidateQueries({ queryKey: queryKeys.models.detail(modelId) });
    },
  });
};

export const usePredict = (modelId: string) =>
  useMutation({
    mutationFn: (inputData: Record<string, unknown>) =>
      modelsService.predict(modelId, inputData).then((r) => r.data),
  });

export const useCreateModel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; framework: string; task: string; workspace_id: string; description?: string; algorithm?: string }) =>
      modelsService.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.models.all }),
  });
};

export const useDeleteModel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => modelsService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.models.all }),
  });
};

// ═══════════════════════════════════════════════
// EXPERIMENT HOOKS
// ═══════════════════════════════════════════════

export const useExperiments = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.experiments.list(params),
    queryFn: () => experimentsService.list(params).then((r) => r.data),
  });

export const useExperiment = (id: string) =>
  useQuery({
    queryKey: queryKeys.experiments.detail(id),
    queryFn: () => experimentsService.get(id).then((r) => r.data),
    enabled: !!id,
  });

export const useExperimentRuns = (experimentId: string, params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.experiments.runs(experimentId),
    queryFn: () => experimentsService.runs.list(experimentId, params).then((r) => r.data),
    enabled: !!experimentId,
  });

export const useRunDetail = (experimentId: string, runId: string) =>
  useQuery({
    queryKey: queryKeys.experiments.runDetail(experimentId, runId),
    queryFn: () => experimentsService.runs.get(experimentId, runId).then((r) => r.data),
    enabled: !!experimentId && !!runId,
  });

export const useLeaderboard = (experimentId: string, metricKey: string, goal?: string) =>
  useQuery({
    queryKey: queryKeys.experiments.leaderboard(experimentId, metricKey),
    queryFn: () => experimentsService.leaderboard(experimentId, metricKey, goal).then((r) => r.data),
    enabled: !!experimentId && !!metricKey,
  });

export const useCreateRun = (experimentId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { run_name?: string; params?: Record<string, unknown> }) =>
      experimentsService.runs.create(experimentId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.experiments.runs(experimentId) });
      qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId) });
    },
  });
};

export const useLogMetric = (experimentId: string, runId: string) =>
  useMutation({
    mutationFn: (data: { key: string; value: number; step?: number }) =>
      experimentsService.runs.logMetric(experimentId, runId, data).then((r) => r.data),
  });

export const useCreateExperiment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; workspace_id: string; description?: string; primary_metric?: string; metric_goal?: string }) =>
      experimentsService.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.experiments.all }),
  });
};

export const useDeleteExperiment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => experimentsService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.experiments.all }),
  });
};

// ═══════════════════════════════════════════════
// PIPELINE HOOKS
// ═══════════════════════════════════════════════

export const usePipelines = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.pipelines.list(params),
    queryFn: () => pipelinesService.list(params).then((r) => r.data),
  });

export const usePipeline = (id: string) =>
  useQuery({
    queryKey: queryKeys.pipelines.detail(id),
    queryFn: () => pipelinesService.get(id).then((r) => r.data),
    enabled: !!id,
  });

export const usePipelineRuns = (pipelineId: string) =>
  useQuery({
    queryKey: queryKeys.pipelines.runs(pipelineId),
    queryFn: () => pipelinesService.runs.list(pipelineId).then((r) => r.data),
    enabled: !!pipelineId,
  });

export const useTriggerRun = (pipelineId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => pipelinesService.runs.trigger(pipelineId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.pipelines.runs(pipelineId) });
      qc.invalidateQueries({ queryKey: queryKeys.pipelines.detail(pipelineId) });
    },
  });
};

export const useCreatePipeline = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; workspace_id: string; description?: string; trigger_type?: string }) =>
      pipelinesService.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.pipelines.all }),
  });
};

export const useDeletePipeline = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelinesService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.pipelines.all }),
  });
};

// ═══════════════════════════════════════════════
// BILLING HOOKS
// ═══════════════════════════════════════════════

export const usePlans = () =>
  useQuery({
    queryKey: queryKeys.billing.plans,
    queryFn: () => billingService.plans.list().then((r) => r.data),
    staleTime: 10 * 60 * 1000, // 10 min
  });

export const useSubscription = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.billing.subscription(workspaceId),
    queryFn: () => billingService.subscriptions.getForWorkspace(workspaceId).then((r) => r.data),
    enabled: !!workspaceId,
  });

export const useUsageDashboard = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.billing.usage(workspaceId),
    queryFn: () => billingService.usage.dashboard(workspaceId).then((r) => r.data),
    enabled: !!workspaceId,
  });

export const useInvoices = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.billing.invoices(workspaceId),
    queryFn: () => billingService.invoices.list(workspaceId).then((r) => r.data),
    enabled: !!workspaceId,
  });

export const usePaymentMethods = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.billing.paymentMethods(workspaceId),
    queryFn: () => billingService.paymentMethods.list(workspaceId).then((r) => r.data),
    enabled: !!workspaceId,
  });

export const useChangePlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, data }: { subscriptionId: string; data: { new_plan_id: string } }) =>
      billingService.subscriptions.changePlan(subscriptionId, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing'] }),
  });
};

export const useCreateCheckoutSession = () =>
  useMutation({
    mutationFn: ({ workspaceId, planId, interval }: { workspaceId: string; planId: string; interval: string }) =>
      billingService.checkout.create(workspaceId, { plan_id: planId, interval }).then((r) => r.data),
  });

export const useCreatePortalSession = () =>
  useMutation({
    mutationFn: (workspaceId: string) =>
      billingService.checkout.portal(workspaceId).then((r) => r.data),
  });

// ═══════════════════════════════════════════════
// NOTIFICATION HOOKS
// ═══════════════════════════════════════════════

export const useNotifications = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: () => notificationsService.list(params).then((r) => r.data),
  });

export const useNotificationCounts = () =>
  useQuery({
    queryKey: queryKeys.notifications.counts,
    queryFn: () => notificationsService.counts().then((r) => r.data),
    refetchInterval: 30 * 1000, // Poll every 30s
  });

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => notificationsService.markRead(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.notifications.counts });
    },
  });
};

export const useMarkAllRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.notifications.counts });
    },
  });
};

export const useNotificationPreferences = () =>
  useQuery({
    queryKey: queryKeys.notifications.preferences,
    queryFn: () => notificationsService.preferences.get().then((r) => r.data),
  });

// ═══════════════════════════════════════════════
// AUDIT HOOKS
// ═══════════════════════════════════════════════

export const useAuditLog = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.audit.list(params),
    queryFn: () => auditService.list(params).then((r) => r.data),
  });

export const useActivityFeed = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.audit.feed(params),
    queryFn: () => auditService.activityFeed(params).then((r) => r.data),
  });

export const useResourceHistory = (resourceType: string, resourceId: string) =>
  useQuery({
    queryKey: ['audit', 'resource', resourceType, resourceId],
    queryFn: () => auditService.resourceHistory(resourceType, resourceId).then((r) => r.data),
    enabled: !!resourceType && !!resourceId,
  });

// ═══════════════════════════════════════════════
// ANALYSIS HOOKS
// ═══════════════════════════════════════════════

export const useAnalyses = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.analyses.list(params),
    queryFn: () => analysesService.list(params).then((r) => r.data),
  });

export const useAnalysis = (id: string) =>
  useQuery({
    queryKey: queryKeys.analyses.detail(id),
    queryFn: () => analysesService.get(id).then((r) => r.data),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'RUNNING' ? 3000 : false;
    },
  });

export const useCreateAnalysis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: T.AnalysisCreate) => analysesService.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.analyses.all }),
  });
};

export const useRetryAnalysis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analysesService.retry(id).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.analyses.all });
      qc.invalidateQueries({ queryKey: queryKeys.analyses.detail(data.id) });
    },
  });
};

export const useDeleteAnalysis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analysesService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.analyses.all }),
  });
};
